import type { Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { COPING } from './gableTrim';
import { hexahedronSolid } from './solids';

/**
 * A light stone slab on each step of a stepped gable, proud of the front and over the riser below it, as the
 * Master of Alkmaar paints the stepped gables of panels 1 and 5 (1504) and the Rokin's print of about 1560
 * draws them (docs/plans/houses.md, section 8.14). The sizes are estimates at the panels' scale (L).
 */
export const STEP_COPING = {
  thickness: 0.1,
  /** Proud of the front, within the 5 cm past its lot line that a house's parts may reach. */
  proud: 0.045,
  overhang: 0.06,
  /** The slab's top stands this far above the coping band's, so the two never share a face. */
  above: 0.02,
} as const;

/** The level runs of a coping path, each step's tread and the crest, with collinear runs joined. */
export function stepTreads(path: readonly Vec3[]): (readonly [Vec3, Vec3])[] {
  const level = (a: Vec3, b: Vec3) => Math.abs(a[1] - b[1]) < 1e-9 && Math.abs(a[2] - b[2]) > 1e-9;
  return path.slice(1).reduce<(readonly [Vec3, Vec3])[]>((runs, b, i) => {
    const a = path[i]!;
    if (!level(a, b)) return runs;
    const last = runs[runs.length - 1];
    if (last && last[1] === a) return [...runs.slice(0, -1), [last[0], b] as const];
    return [...runs, [a, b] as const];
  }, []);
}

/* A crest runs over the middle and reaches past both risers; a step's tread only past its outer end. */
function treadSpan(tread: readonly [Vec3, Vec3], halfWidth: number): readonly [number, number] {
  const [lo, hi] = [Math.min(tread[0][2], tread[1][2]), Math.max(tread[0][2], tread[1][2])];
  const reach = STEP_COPING.overhang;
  const [z0, z1] = lo < 0 && hi > 0 ? [lo - reach, hi + reach] : lo >= 0 ? [lo, hi + reach] : [lo - reach, hi];
  const limit = halfWidth - COPING.sideClearance;
  return [Math.max(-limit, z0), Math.min(limit, z1)];
}

function slab(tread: readonly [Vec3, Vec3], halfWidth: number, xBack: number): Vec3[] {
  const top = tread[0][1] + COPING.lift + STEP_COPING.above;
  const ys = [top - STEP_COPING.thickness, top] as const;
  const xs = [-STEP_COPING.proud, xBack] as const;
  const zs = treadSpan(tread, halfWidth);
  return Array.from({ length: 8 }, (_, i): Vec3 => [xs[i & 1]!, ys[(i >> 1) & 1]!, zs[(i >> 2) & 1]!]);
}

/** One closed slab on each tread of `path`, from `proud` in front of the gable to `xBack` behind it. */
export function buildStepCopings(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  path: readonly Vec3[],
  bounds: { readonly xBack: number; readonly halfWidth: number }
): void {
  for (const tread of stepTreads(path)) {
    hexahedronSolid(builder, style, frame, slab(tread, bounds.halfWidth, bounds.xBack));
  }
}
